/**
 * Attachment Helper Utilities
 * 
 * Centralized file type detection and attachment rendering helpers.
 * Used across Step 5, Step 6, Step 8, and PostGrid payload building.
 */

/** File picker accept string for Step 5 appended attachments and cropper uploads. */
export const SUPPORTED_ATTACHMENT_ACCEPT =
  '.pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,application/pdf,image/png,image/jpeg,image/jpg,image/webp,image/heic,image/heif';

export const SUPPORTED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heic', '.heif', '.webp'];

export const INVALID_IMAGE_UPLOAD_MESSAGE =
  'Upload failed. Please upload a JPG, JPEG, PNG, or HEIC image.';

function normalizeMimeBase(mime) {
  return String(mime || '')
    .toLowerCase()
    .split(';')[0]
    .trim();
}

function fileNameOrUrl(fileOrAttachment) {
  const name = fileOrAttachment?.name || fileOrAttachment?.file_name || fileOrAttachment?.original_file_name || fileOrAttachment?.title || '';
  const url = fileOrAttachment?.url || fileOrAttachment?.preview_url || fileOrAttachment?.original_file_url || fileOrAttachment?.file_url || fileOrAttachment?.cropped_url || '';
  return `${name} ${url}`.toLowerCase().split('?')[0];
}

function hasImageExtension(clean) {
  return /\.(png|jpe?g|webp|gif|bmp|tiff?)$/i.test(clean);
}

function hasHeicExtension(clean) {
  return /\.heic($|[?#])|\.heif($|[?#])/i.test(clean) || clean.endsWith('.heic') || clean.endsWith('.heif');
}

function isStandardImageMime(mimeBase) {
  return (
    mimeBase === 'image/jpeg' ||
    mimeBase === 'image/jpg' ||
    mimeBase === 'image/png' ||
    mimeBase === 'image/webp' ||
    (mimeBase.startsWith('image/') &&
      !mimeBase.includes('heic') &&
      !mimeBase.includes('heif') &&
      mimeBase !== 'image/gif' &&
      mimeBase !== 'image/svg+xml')
  );
}

function isHeicMime(mimeBase) {
  return (
    mimeBase.includes('heic') ||
    mimeBase.includes('heif')
  );
}

/**
 * Detects the file type of an attachment or File object.
 * Supports PDF, PNG, JPG/JPEG, WEBP, HEIC/HEIF
 * 
 * @param {Object|File} fileOrAttachment - The attachment object or File
 * @returns {'pdf' | 'heic' | 'image' | 'unknown'} The detected file type
 */
export function getAttachmentFileType(fileOrAttachment) {
  if (!fileOrAttachment) return 'unknown';
  
  const mime = fileOrAttachment?.type || fileOrAttachment?.mime_type || fileOrAttachment?.content_type || fileOrAttachment?.file_type || '';
  const mimeBase = normalizeMimeBase(mime);
  const clean = fileNameOrUrl(fileOrAttachment);

  // Check for PDF
  if (mimeBase === 'application/pdf' || clean.endsWith('.pdf')) {
    return 'pdf';
  }

  // Standard image MIME wins over misleading HEIC extensions (e.g. converted JPEG still named .heic).
  if (isStandardImageMime(mimeBase) || hasImageExtension(clean)) {
    return 'image';
  }

  // HEIC/HEIF by MIME or extension (including application/octet-stream + .heic/.heif).
  if (isHeicMime(mimeBase) || hasHeicExtension(clean)) {
    return 'heic';
  }

  // Blank or generic MIME — fall back to extension.
  if (!mimeBase || mimeBase === 'application/octet-stream') {
    if (hasHeicExtension(clean)) return 'heic';
    if (hasImageExtension(clean)) return 'image';
  }

  return 'unknown';
}

/**
 * True when the file should be treated as a supported raster upload (JPG/JPEG/PNG/HEIC/HEIF).
 */
export function isSupportedImageUploadFile(file) {
  if (!file) return false;
  const fileType = getAttachmentFileType(file);
  return fileType === 'image' || fileType === 'heic';
}

/**
 * Resolve a reliable MIME type for upload when the browser reports blank or octet-stream.
 */
export function normalizeUploadMimeType(file) {
  if (!file) return 'application/octet-stream';

  const existing = normalizeMimeBase(file.type);
  if (existing && existing !== 'application/octet-stream') {
    return existing === 'image/jpg' ? 'image/jpeg' : existing;
  }

  const name = String(file.name || '').toLowerCase();
  if (/\.jpe?g$/i.test(name)) return 'image/jpeg';
  if (/\.png$/i.test(name)) return 'image/png';
  if (/\.webp$/i.test(name)) return 'image/webp';
  if (/\.heic$/i.test(name)) return 'image/heic';
  if (/\.heif$/i.test(name)) return 'image/heif';

  const fileType = getAttachmentFileType(file);
  if (fileType === 'image') return 'image/jpeg';
  if (fileType === 'heic') return 'image/heic';
  if (fileType === 'pdf') return 'application/pdf';

  return existing || 'application/octet-stream';
}

/**
 * Re-wrap a File/Blob with a normalized MIME type and preserved filename.
 */
export function wrapUploadFileWithMime(file) {
  if (!file) return file;

  const type = normalizeUploadMimeType(file);
  const name = file.name || 'document';
  if (file instanceof File && file.type === type && file.name === name) {
    return file;
  }

  return new File([file], name, {
    type,
    lastModified: file.lastModified || Date.now(),
  });
}

/**
 * Build a cropped upload File with MIME type aligned to the filename extension.
 */
export function createCroppedUploadFile(blob, fileName, fallbackType = 'image/jpeg') {
  const type =
    blob?.type && blob.type !== 'application/octet-stream'
      ? blob.type
      : /\.png$/i.test(fileName)
        ? 'image/png'
        : fallbackType || 'image/jpeg';
  const normalizedType = type === 'image/jpg' ? 'image/jpeg' : type;
  return new File([blob], fileName, {
    type: normalizedType,
    lastModified: Date.now(),
  });
}

/**
 * Checks if a File or attachment is a PDF.
 * 
 * @param {Object|File} fileOrAttachment - The attachment object or File
 * @returns {boolean} True if PDF
 */
export function isPdfFile(fileOrAttachment) {
  return getAttachmentFileType(fileOrAttachment) === 'pdf';
}

/**
 * Checks if a File or attachment is HEIC/HEIF.
 * 
 * @param {Object|File} fileOrAttachment - The attachment object or File
 * @returns {boolean} True if HEIC/HEIF
 */
export function isHeicFile(fileOrAttachment) {
  return getAttachmentFileType(fileOrAttachment) === 'heic';
}

/**
 * Checks if a File or attachment is a standard image (PNG, JPG, WEBP, etc).
 * 
 * @param {Object|File} fileOrAttachment - The attachment object or File
 * @returns {boolean} True if standard image
 */
export function isImageFile(fileOrAttachment) {
  return getAttachmentFileType(fileOrAttachment) === 'image';
}

/**
 * Gets the best available URL for an attachment.
 * For cropped documents, prefers cropped_url.
 * 
 * @param {Object} attachment - The attachment object
 * @returns {string|null} The best URL to use
 */
export function getAttachmentUrl(attachment) {
  if (!attachment) return null;
  
  // Always prefer the confirmed cropped URL if it exists
  if (attachment.cropped_url) {
    return attachment.cropped_url;
  }
  
  return attachment.preview_url || attachment.file_url || attachment.url || null;
}

/**
 * Gets a clean display label for an attachment type.
 * 
 * @param {Object} attachment - The attachment object
 * @returns {string} A clean, user-friendly label
 */
export function getCleanAttachmentLabel(attachment) {
  const type = String(
    attachment?.type ||
    attachment?.document_type ||
    attachment?.category ||
    attachment?.label ||
    ''
  ).toLowerCase();

  if (type.includes('proof') || type.includes('address')) {
    return 'Proof of Address';
  }
  if (type.includes('government') || type.includes('gov_id') || type === 'id') {
    return 'Government ID';
  }
  if (type.includes('breach')) {
    return 'Data Breach Proof';
  }
  if (type.includes('ssn') || type.includes('social')) {
    return 'SSN Card';
  }
  
  return 'Supporting Document';
}

/**
 * Prepares an attachment object for the final PDF/PostGrid payload.
 * 
 * @param {Object} attachment - The raw attachment object
 * @returns {Object} A normalized attachment object
 */
export function normalizeAttachmentForPayload(attachment) {
  const url = getAttachmentUrl(attachment);
  const label = getCleanAttachmentLabel(attachment);
  
  return {
    type: attachment.type || attachment.category || 'supporting_document',
    label,
    file_url: url,
    cropped_url: attachment.cropped_url || null,
    mime_type: attachment.mime_type || attachment.content_type || 'image/jpeg',
    document_id: attachment.document_id || attachment.id,
    source_file_type: attachment.source_file_type || 'image',
    selected_pdf_page_number: attachment.selected_pdf_page_number || null,
  };
}

/**
 * Checks if the attachment was originally a PDF that was cropped to an image.
 * 
 * @param {Object} attachment - The attachment object
 * @returns {boolean} True if the source was PDF
 */
export function isOriginallyPdf(attachment) {
  return attachment?.source_file_type === 'pdf' || attachment?.is_from_pdf === true;
}

export default {
  getAttachmentFileType,
  isPdfFile,
  isHeicFile,
  isImageFile,
  isSupportedImageUploadFile,
  normalizeUploadMimeType,
  wrapUploadFileWithMime,
  createCroppedUploadFile,
  INVALID_IMAGE_UPLOAD_MESSAGE,
  getAttachmentUrl,
  getCleanAttachmentLabel,
  normalizeAttachmentForPayload,
  isOriginallyPdf,
};
